import { isArray, isNumber, values } from "lodash";
import {
    Audit,
    CancellationProtection,
    CancellationProtectionStatus,
    ExperiencePurchaseItem,
    Purchase,
    PurchaseItem,
} from "types";

const CLAIM_STATUSES = new Set<string>([
    CancellationProtectionStatus.CLAIM_SUBMITTED,
    CancellationProtectionStatus.CLAIM_PROCESSED,
    CancellationProtectionStatus.REFUND_FAILED,
    CancellationProtectionStatus.CLAIM_UNDER_REVIEW,
    CancellationProtectionStatus.NEEDS_MANUAL_REFUND,
]);

const CLAIMED_STATUSES = new Set<string>([...CLAIM_STATUSES, CancellationProtectionStatus.UNUSED]);

export const isClaimStatus = (status?: string) => !!status && CLAIM_STATUSES.has(status);

export const isProcessedClaimStatus = (status?: string) => status === CancellationProtectionStatus.CLAIM_PROCESSED;

export const hasClaimedCancellationProtection = (item?: Pick<ExperiencePurchaseItem, "cancellationProtection">) => {
    const status = item?.cancellationProtection?.status;

    return !!status && CLAIMED_STATUSES.has(status);
};

export const isSettledByCancellationProtection = (purchase: Pick<Purchase, "items">, item?: PurchaseItem) => {
    return item
        ? hasClaimedCancellationProtection(item)
        : purchase.items.length > 0 &&
              purchase.items.every((purchaseItem) => hasClaimedCancellationProtection(purchaseItem));
};

interface CancellationProtectionChange {
    old?: CancellationProtection;
    new?: CancellationProtection;
}

const findCancellationProtectionChange = (
    item: Pick<PurchaseItem, "id">,
    audit?: Pick<Audit, "data">,
): CancellationProtectionChange | null => {
    const items = audit?.data?.items;
    if (!items) {
        return null;
    }

    const auditItems = isArray(items) ? items : values(items);

    return auditItems.find((entry: any) => entry?.id === item?.id)?.cancellationProtection ?? null;
};

export const getSubmittedCancellationProtectionClaim = (
    item: Pick<PurchaseItem, "id">,
    audit?: Pick<Audit, "data">,
): CancellationProtection | null => {
    const change = findCancellationProtectionChange(item, audit);
    const next = change?.new;

    if (!isClaimStatus(next?.status) || next?.status === change?.old?.status) {
        return null;
    }

    return next ?? null;
};

/**
 * Amount refunded in this change. Default to drop the amount from the copy if not present
 */
export const getAuditRefundedAmount = (
    item: Pick<PurchaseItem, "id">,
    audit?: Pick<Audit, "data">,
): number | undefined => {
    const change = findCancellationProtectionChange(item, audit);
    const next = change?.new?.amountRefunded;

    if (!isNumber(next)) {
        return undefined;
    }

    return next - (change?.old?.amountRefunded ?? 0);
};
